import React, { useState } from "react";
import PropTypes from "prop-types";
import { css } from "@emotion/react";
import { FormattedMessage } from "react-intl";
import { ROLE_OPTIONS, FormRole, FormExperience, FormEmail, SignupComplete } from "components/Signup";
import { useLinks, useSignupForm } from "hooks";
import { colors, fontSize } from "style";
import { Col, Row } from "components/Containers";
import { PrimaryLink } from "components/Links";

/**
 * SignupForms
 *
 * @param {Function} onSignupCompleted
 */
const SignupForms = ({ onSignupCompleted }) => {
  const links = useLinks();
  const { formData, handleFormChange, signupCompleted, setSignupCompleted } = useSignupForm({ onSignupCompleted });
  const [step, setStep] = useState(1);

  if (signupCompleted) {
    return <SignupComplete formData={formData} />;
  }

  function getForm() {
    let { role, ...rest } = formData;

    switch (step) {
      default:
        return (
          <Col>
            <FormRole
              values={formData}
              onChange={handleFormChange}
              onSubmit={(payload) => {
                handleFormChange(payload);
                setStep(2);
              }}
            />
            <span css={styles.already_member}>
              <FormattedMessage
                id="Signup.SignupHeader.Text"
                values={{
                  link: (
                    <PrimaryLink href={links.login} routerEnabled>
                      <FormattedMessage id="Signup.SignupHeader.Link" />
                    </PrimaryLink>
                  ),
                }}
              />
            </span>
          </Col>
        );

      case 2:
        return (
          <FormExperience
            values={formData}
            onChange={handleFormChange}
            onCancel={() => {
              setStep(1);
            }}
            onSubmit={(payload) => {
              handleFormChange(payload);
              setStep(3);
            }}
          />
        );

      case 3:
        return (
          <FormEmail
            values={{
              ...rest,
              ...{
                archetype: findArchetypeByRole(role),
              },
            }}
            onCompleted={() => {
              setSignupCompleted(true);
            }}
            onChange={handleFormChange}
            onCancel={() => {
              setStep(2);
            }}
          />
        );
    }
  }

  return (
    <>
      <Row css={styles.container}>
        <span css={styles.title}>
          <FormattedMessage id="Signup.SignupForms.Title" />
        </span>
        <span css={styles.counter}>
          <FormattedMessage id="Signup.StepCounter" values={{ step, total: 3 }} />
        </span>
      </Row>
      <div css={styles.container}>{getForm()}</div>
    </>
  );
};

function findArchetypeByRole(role) {
  const selectedRole = ROLE_OPTIONS.filter((item) => item.value === role);

  return selectedRole && selectedRole[0].archetype;
}

const styles = {
  container: css`
    justify-content: space-between;
    margin-bottom: 1.5rem;
  `,
  title: css`
    color: ${colors.black};
    font-size: ${fontSize.xxlarge};
    font-weight: 700;
  `,
  counter: css`
    font-size: ${fontSize.normal};
    color: ${colors.grayAnatomyLight1};
    font-weight: 300;
  `,
  subtitle: css`
    font-size: ${fontSize.normal};
    color: ${colors.grayAnatomyLight1};
    font-weight: 500;
    margin-bottom: 3rem;
  `,
  already_member: css`
    font-size: ${fontSize.small};
    color: ${colors.grayAnatomyLight1};
    margin: 3rem auto 0 auto;
  `,
};

SignupForms.propTypes = {
  onSignupCompleted: PropTypes.func,
};

export default SignupForms;
